<script lang="ts" setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import {
    ChevronUpDownIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    MagnifyingGlassIcon,
} from "@heroicons/vue/24/outline";

type SortKey = "name" | "status" | "policy" | "datum";
type Direction = "asc" | "desc";

const props = defineProps<{
    indexRoute?: string;
}>();

const sort = ref<SortKey>("name");
const direction = ref<Direction>("asc");
const perPage = ref<number>(20);
const search = ref<string>("");

const openSort = ref(false);
const dropdownEl = ref<HTMLElement | null>(null);
const allowedPerPage = [20, 50, 100];

onMounted(() => {
    const params = new URLSearchParams(window.location.search);
    const urlSort = params.get("sort_by") as SortKey | null;
    const urlDir = params.get("sort_by_direction") as Direction | null;
    const urlPer = Number(params.get("per_page"));
    const urlSearch = params.get("search");

    if (urlSort) sort.value = urlSort;
    if (urlDir === "asc" || urlDir === "desc") direction.value = urlDir;
    if (allowedPerPage.includes(urlPer)) perPage.value = urlPer;
    if (urlSearch !== null) search.value = urlSearch;

    document.addEventListener("click", handleOutside, true);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", handleOutside, true);
});

function handleOutside(e: MouseEvent) {
    if (!openSort.value) return;
    const t = e.target as Node | null;
    if (dropdownEl.value && t && !dropdownEl.value.contains(t)) {
        openSort.value = false;
    }
}

function pushQuery(replace = true) {
    const params: Record<string, unknown> = {
        sort_by: sort.value,
        sort_by_direction: direction.value,
        per_page: perPage.value,
    };
    if (search.value) params.search = search.value;

    router.get(route(props.indexRoute ?? "domains.index"), params, {
        preserveState: true,
        preserveScroll: true,
        replace,
    });
}

let timer: number | undefined;
watch(search, () => {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(() => pushQuery(), 300);
});

watch([sort, direction, perPage], () => pushQuery());

function setSort(key: SortKey) {
    if (sort.value === key) {
        direction.value = direction.value === "asc" ? "desc" : "asc";
    } else {
        sort.value = key;
        direction.value = "asc";
    }
    openSort.value = false;
}

function bumpPerPage(delta: number) {
    const idx = allowedPerPage.indexOf(perPage.value);
    const next =
        allowedPerPage[
            Math.min(Math.max(idx + delta, 0), allowedPerPage.length - 1)
        ];
    perPage.value = next;
}
</script>

<template>
    <div class="flex w-full items-center justify-between gap-3">
        <!-- Sort dropdown -->
        <div ref="dropdownEl" class="relative">
            <button
                type="button"
                class="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                @click="openSort = !openSort"
            >
                <svg
                    class="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 4h13M3 8h9M3 12h5"
                    />
                </svg>
                <span>{{ $t("vue.sort_by") }}</span>
                <ChevronUpDownIcon class="h-4 w-4" />
            </button>

            <div
                v-show="openSort"
                class="absolute z-10 mt-2 w-56 rounded-md border border-gray-200 bg-white p-1 shadow-lg"
            >
                <button
                    class="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm hover:bg-gray-50"
                    :class="{ 'bg-gray-100': sort === 'domain' }"
                    @click="setSort('domain')"
                >
                    <span>{{ $t("vue.domain_name") }}</span>
                    <span v-if="sort === 'domain'">
                        <ChevronUpIcon
                            v-if="direction === 'asc'"
                            class="h-4 w-4"
                        />
                        <ChevronDownIcon v-else class="h-4 w-4" />
                    </span>
                </button>
                <button
                    class="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm hover:bg-gray-50"
                    :class="{ 'bg-gray-100': sort === 'active' }"
                    @click="setSort('active')"
                >
                    <span>{{ $t("vue.status") }}</span>
                    <span v-if="sort === 'active'">
                        <ChevronUpIcon
                            v-if="direction === 'asc'"
                            class="h-4 w-4"
                        />
                        <ChevronDownIcon v-else class="h-4 w-4" />
                    </span>
                </button>
                <button
                    class="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm hover:bg-gray-50"
                    :class="{ 'bg-gray-100': sort === 'p' }"
                    @click="setSort('p')"
                >
                    <span>{{ $t("vue.dmarc_policy") }}</span>
                    <span v-if="sort === 'p'">
                        <ChevronUpIcon
                            v-if="direction === 'asc'"
                            class="h-4 w-4"
                        />
                        <ChevronDownIcon v-else class="h-4 w-4" />
                    </span>
                </button>
                <button
                    class="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm hover:bg-gray-50"
                    :class="{ 'bg-gray-100': sort === 'created_at' }"
                    @click="setSort('created_at')"
                >
                    <span>{{ $t("vue.date") }}</span>
                    <span v-if="sort === 'created_at'">
                        <ChevronUpIcon
                            v-if="direction === 'asc'"
                            class="h-4 w-4"
                        />
                        <ChevronDownIcon v-else class="h-4 w-4" />
                    </span>
                </button>
            </div>
        </div>

        <!-- Per-page number input -->
        <div class="flex items-center">
            <input
                v-model.number="perPage"
                type="number"
                class="w-20 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                :min="20"
                :max="100"
                step="10"
                @blur="perPage = [20, 50, 100].includes(perPage) ? perPage : 20"
            />
        </div>

        <!-- Search -->
        <div class="ml-auto w-full max-w-xl">
            <div class="relative">
                <MagnifyingGlassIcon
                    class="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                />
                <input
                    v-model="search"
                    type="search"
                    :placeholder="'Search domains'"
                    class="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
            </div>
        </div>
    </div>
</template>
